import {
  declaredElementCount as dataTypeDeclaredElementCount,
  hasMultipleArrayDimensions,
} from "@/shared/domain/topics";

import { assignSeriesColor } from "./assignSeriesColor";
import { groupSlice, idForSeries, isGroupedSeries } from "./identifiers";
import {
  type GroupedPlotSeries,
  type PlotSeries,
  type PlotSeriesMember,
  type PlotSeriesMemberConfig,
  type PlotSeriesMemberDefaults,
  type TopicData,
} from "./schema";
import { type PlotYAxis } from "./schema/v1";

/**
 * The most elements a declared array may have for every one of them to become
 * a member when the series is added: a whole array declaring an element count
 * up to the threshold gets one member per element on add; one declaring more,
 * or none, gets no members until the first read, which fills the first
 * {@link MAX_GROUP_MEMBERS} elements it finds. The plot's truncation notice
 * and its element picker appear whenever a read finds more elements than the
 * group has members, whatever the threshold. 25 is a starting value, chosen so
 * a typical joint or tracking array (7 to 12 elements) fits and a laser scan
 * (hundreds) does not; it is expected to become a workspace setting.
 */
export const DEFAULT_GROUP_MEMBER_THRESHOLD = 25;

/**
 * The most members a group holds, however many elements its array has: the
 * most chart datasets one series or group may hold, which keeps an array with
 * many elements from swamping the chart. The plot data streamer's
 * `maxDatasetsPerSeries` reads this constant. 36 bounds the legend, the
 * sidebar and the per-render cost of one series. It holds a ROS 6x6
 * covariance in full (the `float64[36]` of odometry and pose-with-covariance
 * messages), and clears the 25-member group threshold
 * ({@link DEFAULT_GROUP_MEMBER_THRESHOLD}) and the longest other fixed-length
 * array in customer recordings (30 elements). Not profiled.
 */
export const MAX_GROUP_MEMBERS = 36;

/**
 * Whether the plot panel holds `data` as a grouped series: a whole-array or
 * range path (see {@link groupSlice}) that is not a matrix. A
 * multi-dimensional array's elements are rows, which no message-path grammar
 * addresses, so it stays a single series, its elements flattened into one
 * line.
 */
export function isGroupablePath(data: TopicData): boolean {
  const dataType = data.messagePath.nativeDataType;
  return (
    groupSlice(data) !== undefined &&
    (dataType === undefined || !hasMultipleArrayDimensions(dataType))
  );
}

/** The config a member at `index` starts with under `defaults`. */
function seededConfig(
  defaults: PlotSeriesMemberDefaults,
  index: number,
): PlotSeriesMemberConfig {
  return defaults.label === undefined
    ? { ...defaults }
    : { ...defaults, label: `${defaults.label}[${index}]` };
}

/**
 * Fresh members for `indices`, in that order, each visible, and configured
 * from `defaults` when the group has any.
 */
export function makeMembers(
  indices: number[],
  defaults?: PlotSeriesMemberDefaults,
): PlotSeriesMember[] {
  return indices.map((index) => ({
    id: idForSeries(),
    index,
    visible: true,
    ...(defaults !== undefined
      ? { config: seededConfig(defaults, index) }
      : {}),
  }));
}

/**
 * The members that `elementCount` elements from `start` give a group: one per
 * element, up to the first {@link MAX_GROUP_MEMBERS}.
 */
export function membersFrom(
  start: number,
  elementCount: number,
  defaults?: PlotSeriesMemberDefaults,
): Pick<GroupedPlotSeries, "members"> {
  return {
    members: makeMembers(
      Array.from(
        { length: Math.min(elementCount, MAX_GROUP_MEMBERS) },
        (_, i) => start + i,
      ),
      defaults,
    ),
  };
}

/**
 * The members a group starts with. A range (`axes[3:6]`) names them itself,
 * by absolute index (see {@link membersFrom}), stopping at the element count
 * its record declares when it declares one (`axes[3:9]` on a `float64[7]`
 * names 3 to 6). A whole array gets one per element when its record declares
 * an element count within {@link DEFAULT_GROUP_MEMBER_THRESHOLD}, otherwise
 * none, and the first read fills them in.
 */
export function defaultMembers(
  data: TopicData,
  defaults?: PlotSeriesMemberDefaults,
): Pick<GroupedPlotSeries, "members"> {
  const range = groupSlice(data);
  const elementCount = declaredElementCount(data);
  if (range?.end !== undefined) {
    const end =
      elementCount === undefined
        ? range.end
        : Math.min(range.end, elementCount);
    return membersFrom(range.start, Math.max(0, end - range.start), defaults);
  }
  if (
    elementCount === undefined ||
    elementCount > DEFAULT_GROUP_MEMBER_THRESHOLD
  ) {
    return { members: [] };
  }
  return membersFrom(0, elementCount, defaults);
}

/**
 * The element indices a group's members may take, `end` exclusive: a range's
 * own, else from 0 to the element count the record declares, or with no end
 * when it declares none.
 */
export function memberBounds(data: TopicData): { start: number; end?: number } {
  const range = groupSlice(data);
  return range?.end !== undefined
    ? range
    : { start: 0, end: declaredElementCount(data) };
}

/**
 * The member indices a group may hold out of `indices`: each an integer in
 * `[start, end)` (from `start` on when `end` is undefined), the first of any
 * repeat, and at most {@link MAX_GROUP_MEMBERS} of them, in the order given.
 * The element picker and a change of data source bound a group's members by
 * {@link memberBounds}; a layout's stored member list is bound by the path's
 * range alone ({@link groupSlice}), since a member past the recording's
 * declared element count keeps its row and draws nothing.
 */
export function validMemberIndices(
  indices: number[],
  { start, end }: { start: number; end?: number },
): number[] {
  const valid = indices.filter(
    (index) =>
      Number.isInteger(index) &&
      index >= start &&
      (end === undefined || index < end),
  );
  return [...new Set(valid)].slice(0, MAX_GROUP_MEMBERS);
}

/** The element count `data`'s record declares, if it declares one. */
export function declaredElementCount(data: TopicData): number | undefined {
  const dataType = data.messagePath.nativeDataType;
  return dataType === undefined
    ? undefined
    : dataTypeDeclaredElementCount(dataType);
}

/**
 * The entry the plot panel holds for `data`: a group for an array path, a
 * single series with the panel's next color for anything else. A group starts
 * with its default members (see {@link defaultMembers}), marked
 * `membersAreDefault`, each styled from `memberDefaults` when given; the group
 * keeps `memberDefaults` as its `defaultMemberStyle` (see
 * {@link GroupedPlotSeries}).
 */
export function makePlotSeries(
  panelId: string,
  data: TopicData,
  id: string = idForSeries(),
  memberDefaults?: PlotSeriesMemberDefaults,
): PlotSeries {
  if (isGroupablePath(data)) {
    return {
      kind: "group",
      id,
      data,
      visible: true,
      ...defaultMembers(data, memberDefaults),
      membersAreDefault: true,
      ...(memberDefaults !== undefined
        ? { defaultMemberStyle: memberDefaults }
        : {}),
    };
  }
  return {
    id,
    data,
    visible: true,
    config: { lineColor: assignSeriesColor(panelId, data) },
  };
}

/**
 * Every y-axis some entry or member of `data` plots on, the default included;
 * the panel drops its settings for any extra axis not in the set. A group's
 * `defaultMemberStyle` counts too: a group opened from a layout rule can have
 * no members until its first read, and the members that read fills in plot on
 * that style's axis.
 */
export function usedYAxes(data: PlotSeries[]): Set<PlotYAxis> {
  const used = new Set<PlotYAxis>(["y"]);
  for (const entry of data) {
    const configs = isGroupedSeries(entry)
      ? [
          entry.defaultMemberStyle,
          ...entry.members.map((member) => member.config),
        ]
      : [entry.config];
    for (const config of configs) {
      if (config?.yAxis) {
        used.add(config.yAxis);
      }
    }
  }
  return used;
}
